import pino from 'pino';

import HttpRepository from './httpRepository';
import { SessionRepository } from './sessionRepository';

import type { User } from '../zustandStore/slices';
import type { Approval } from './approvalRepository';
import type { Requester } from './requestRepository';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';

const logger = pino();

type GetEventsOptions = {
	conversationId: string;
	after?: string;
	polling?: boolean;
};

type MessageResponse = {
	highestEventSequenceNumber: number;
	messageId: number;
};

type AddMessageOptions = {
	conversationId: string;
	message: string;
};

type EventAuthor = {
	avatarUrl: string;
	companyAccountId: string;
	email: string;
	firstName: string;
	fullName: string;
	initials: string;
	language: string;
	lastName: string;
	state: string;
	userId: string;
	isGo?: boolean;
};

type Person = {
	Created: string;
	FirstName: string;
	LastName: string;
	Email: string;
};

type InitiatorUser = {
	Id: string;
	Person: Person;
	CompanyAccountId: string;
	UserName: string;
	Username: string;
	Credentials: any[];
	State: string;
	Language: string;
	Kind: string;
	TimeZone: string;
};

type Body = {
	data: any;
	type: string;
};

type Data = {
	amount: string | number;
	attachments: [];
	businessId: string;
	cardType: string;
	coding: any;
	currency: string;
	fields: any;
	isArchived: false;
	lastEdit: string;
	profileId: string;
	authPaddingPercentage: 0;
	requestId: string;
	title: string;
	activity?: string;
	body?: Body;
	contentType?: string;
	id?: number;
	initiatorUser?: InitiatorUser;
	state?: string;
	teamId: string;
	task?: Approval;
	requester?: Requester;
};

export type ConversationEvent = {
	author: EventAuthor;
	data: Data;
	messageId: number;
	requestId: string;
	sequenceNumber: number;
	timestamp: number;
	type: string;
	manager?: User;
	reassignedTo?: User;
};

export type GetEventsResponseObject = {
	events: ConversationEvent[];
	lastTimestamp: number;
};

export type GetStreamOptions = {
	after: ReturnType<typeof Date.now>;
	signal?: AbortSignal;
};

function _buildUrl(conversationId: string) {
	return ['/rest', 'conversations', conversationId, 'events'].join('/');
}

function _buildStreamUrl() {
	return ['/rest', 'conversations', 'stream'].join('/');
}

export const ConversationRepository = {
	addMessage: (options: AddMessageOptions) =>
		HttpRepository.tradeshiftGo()
			.put(_buildUrl(options.conversationId), options)
			.then((response: AxiosResponse<MessageResponse>) => {
				return response.data;
			}),

	getEvents: async (options: GetEventsOptions) => {
		if (!options.conversationId) {
			logger.error('conversationId is required');
			await SessionRepository.logout();
			globalThis.location.pathname = '/signin/login';
		}
		const config: AxiosRequestConfig = {
			params: {
				after: options.after,
				polling: options.polling,
			},
		};
		const response: AxiosResponse<GetEventsResponseObject> = await HttpRepository.tradeshiftGo().get(
			_buildUrl(options.conversationId),
			config,
		);
		return response.data;
	},

	getStream: async (options: GetStreamOptions) => {
		const config: AxiosRequestConfig = {
			params: {
				after: options.after,
			},
		};
		const response: AxiosResponse<GetEventsResponseObject> = await HttpRepository.tradeshiftGo().get(
			_buildStreamUrl(),
			config,
		);
		return response.data;
	},
};
